import { useEffect } from 'react'
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Monitoring from './pages/Monitoring'
import Backtest from './pages/Backtest'
import Positions from './pages/Positions'
import Settings from './pages/Settings'
import LiveTrading from './pages/LiveTrading'
import Login from './pages/Login'
import { WebSocketProvider } from './hooks/useWebSocket'
import { useAuthStore } from './hooks/useAuthStore'
import { Activity } from 'lucide-react'

function ProtectedRoute() {
  const { isAuthenticated, loading, authEnabled } = useAuthStore()

  if (loading || authEnabled === null) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Activity size={24} className="text-blue-400 animate-spin" />
      </div>
    )
  }

  if (authEnabled && !isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
}

function PageGuard({ page, children }: { page: string; children: React.ReactNode }) {
  const { role, allowedPages } = useAuthStore()

  if (role === 'admin' || allowedPages.includes(page)) {
    return <>{children}</>
  }

  return <Navigate to="/" replace />
}

function AppRoutes() {
  const navigate = useNavigate()
  const { isAuthenticated, authEnabled } = useAuthStore()

  // Redirect from login if already authenticated
  useEffect(() => {
    if (isAuthenticated && authEnabled) {
      // If on login page and already authenticated, redirect to home
    }
  }, [isAuthenticated, authEnabled, navigate])

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated && authEnabled ? <Navigate to="/" replace /> : <Login />
      } />
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout><Outlet /></Layout>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/live" element={<PageGuard page="live"><LiveTrading /></PageGuard>} />
          <Route path="/monitoring" element={<PageGuard page="monitoring"><Monitoring /></PageGuard>} />
          <Route path="/backtest" element={<PageGuard page="backtest"><Backtest /></PageGuard>} />
          <Route path="/positions" element={<PageGuard page="positions"><Positions /></PageGuard>} />
          <Route path="/settings" element={<PageGuard page="settings"><Settings /></PageGuard>} />
        </Route>
      </Route>
    </Routes>
  )
}

function App() {
  const { restoreSession } = useAuthStore()

  useEffect(() => {
    restoreSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, [])

  return (
    <WebSocketProvider>
      <AppRoutes />
    </WebSocketProvider>
  )
}

export default App
